"use client";

import { c as _c } from "react/compiler-runtime";
import { useEffect, useRef } from "react";
import { Skeleton } from "@/components/ui/skeleton";
import { toEcommerceProduct, trackProductListViewed, trackProductsSearched } from "@/lib/analytics/segment";
import { scrubQueryClient } from "@/lib/search/clientPiiScrub";
import { useSearchSuggestions } from "@/queries/useSearchSuggestions";
import { SearchNoResults } from "./SearchNoResults";
import { SearchProductHitList } from "./SearchProductHitList";
import { SearchProductRequestCta } from "./SearchProductRequestCta";
import { SearchUnavailable } from "./SearchUnavailable";
import { SearchViewAllLink } from "./SearchViewAllLink";
interface Props {
  query: string;
}
function AutocompleteSkeleton() {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <div className="flex flex-col gap-2" data-testid="search-autocomplete-skeleton">{[0, 1, 2].map(_temp)}</div>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
}
function _temp(key) {
  return <Skeleton key={key} className="h-16 w-full" />;
}
export function SearchAutocomplete(t0) {
  const $ = _c(22);
  const {
    query
  } = t0;
  const {
    data,
    isLoading,
    isError,
    refetch
  } = useSearchSuggestions(query);
  const trackedQueryRef = useRef(null);
  let t1;
  let t2;
  if ($[0] !== data || $[1] !== isError || $[2] !== isLoading || $[3] !== query) {
    t1 = () => {
      if (isLoading || isError || !data) {
        return;
      }
      if (trackedQueryRef.current === query) {
        return;
      }
      trackedQueryRef.current = query;
      trackProductsSearched({
        query: scrubQueryClient(query)
      });
      if (data.kind === "results" && data.products.length > 0) {
        trackProductListViewed({
          listId: "search_results",
          products: data.products.map(_temp2)
        });
      }
    };
    t2 = [query, data, isLoading, isError];
    $[0] = data;
    $[1] = isError;
    $[2] = isLoading;
    $[3] = query;
    $[4] = t1;
    $[5] = t2;
  } else {
    t1 = $[4];
    t2 = $[5];
  }
  useEffect(t1, t2);
  if (isError) {
    let t3;
    if ($[6] !== refetch) {
      t3 = <SearchUnavailable onRetry={() => refetch()} />;
      $[6] = refetch;
      $[7] = t3;
    } else {
      t3 = $[7];
    }
    return t3;
  }
  if (isLoading || !data) {
    let t3;
    if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
      t3 = <AutocompleteSkeleton />;
      $[8] = t3;
    } else {
      t3 = $[8];
    }
    return t3;
  }
  if (data.kind === "product-request") {
    let t3;
    if ($[9] !== data.keyword || $[10] !== query) {
      t3 = <SearchProductRequestCta keyword={data.keyword} query={query} />;
      $[9] = data.keyword;
      $[10] = query;
      $[11] = t3;
    } else {
      t3 = $[11];
    }
    return t3;
  }
  if (data.products.length === 0) {
    let t3;
    if ($[12] !== query) {
      t3 = <SearchNoResults query={query} />;
      $[12] = query;
      $[13] = t3;
    } else {
      t3 = $[13];
    }
    return t3;
  }
  let t3;
  if ($[14] !== data.products) {
    t3 = <SearchProductHitList hits={data.products} />;
    $[14] = data.products;
    $[15] = t3;
  } else {
    t3 = $[15];
  }
  let t4;
  if ($[16] !== data.total || $[17] !== query) {
    t4 = <SearchViewAllLink query={query} total={data.total} />;
    $[16] = data.total;
    $[17] = query;
    $[18] = t4;
  } else {
    t4 = $[18];
  }
  let t5;
  if ($[19] !== t3 || $[20] !== t4) {
    t5 = <div className="flex flex-col gap-2" data-testid="search-autocomplete">{t3}{t4}</div>;
    $[19] = t3;
    $[20] = t4;
    $[21] = t5;
  } else {
    t5 = $[21];
  }
  return t5;
}
function _temp2(hit, index) {
  return toEcommerceProduct(hit, index + 1);
}